import { actions, types } from "reducers/contactDestination";
import { call, put, takeLatest } from "redux-saga/effects";
import { types as debinTypes } from "reducers/debin";
import { push, replace } from "connected-react-router";
import * as form from "middleware/form";
import * as frequentDestinationMidleware from "middleware/contactDestination";
import * as transfersMiddleware from "middleware/transfers";
import { actions as notificationActions } from "reducers/notification";
import { selectors as sessionSelectors } from "reducers/session";
import { delay } from "redux-saga";
import { store } from "store";
import { downloadPdf } from "util/download";
import * as i18n from "util/i18n";
import { INSUFFICIENT_SIGNATURES } from "util/responses";
import { credentialsErrorMessage } from "util/validateMessage";

const sagas = [
    takeLatest(types.LOAD_LIST_REQUEST, loadListRequest),
    takeLatest(types.LOAD_DETAIL_REQUEST, loadDetailRequest),
    takeLatest(types.FREQUENT_DESTINATION_CREATE_PREVIEW, frequentDestinationCreatePreview),
    takeLatest(types.FREQUENT_DESTINATION_CREATE_REQUEST, frequentDestinationCreate),
    takeLatest(types.DELETE_FREQUENT_DESTINATION_PREVIEW_REQUEST, deleteFrequentDestinationPreview),
    takeLatest(types.DELETE_FREQUENT_DESTINATION_REQUEST, deleteFrequentDestination),
    takeLatest(types.MODIFY_FREQUENT_DESTINATION_PRE_REQUEST, modifyFrequentDestinationPre),
    takeLatest(types.MODIFY_FREQUENT_DESTINATION_PREVIEW_REQUEST, modifyFrequentDestinationPreview),
    takeLatest(types.MODIFY_FREQUENT_DESTINATION_REQUEST, modifyFrequentDestinationRequest),
    takeLatest(types.FREQUENT_DESTINATION_CREATE_TRANSFER_PRE_REQUEST, createFrequentDestinationTransferPre),
    takeLatest(types.FREQUENT_DESTINATION_REQUEST, verifyFrequentDestination),
    takeLatest(types.GET_CBU_ACCOUNT_INFORMATION, getCbuAccountInformation),
    takeLatest(types.FREQUENT_DESTINATIONS_DOWLOAD, dowloadFrequentDestinations),
    takeLatest(types.DOWNLOAD_DESTINATIONS, downloadDestinations),
    takeLatest(types.VALIDATE_CBU_BLOCKED, validateCbuBlocked),
];

const ticketStates = {
    COR020W: "ERROR",
    COR023I: "PENDING",
    COR000I: "FINISHED",
};

export default sagas;

function* loadListRequest({ filters = "", pageNo }) {
    const { id: idEnvironment } = sessionSelectors.getActiveEnvironment(store.getState());
    const response = yield call(frequentDestinationMidleware.getListFrequentDestinations, {
        nameSearch: filters,
        pageNumber: pageNo,
    });
    if (response.type === "W") {
        yield put(actions.loadListFailure());
        yield put(
            notificationActions.showNotification(i18n.get("global.unexpectedError"), "error", ["FrequentDestinations"]),
        );
    } else {
        yield put({
            type: types.LOAD_LIST_SUCCESS,
            idEnvironment,
            frequentDestinations: response.data.data.frequentDestinations,
            moreStatements: response.data.data.moreStatements,
            pageNumber: response.data.data.pageNumber,
            totalPages: response.data.data.totalPages,
        });
    }
}

function* loadDetailRequest({ id = "" }) {
    const response = yield call(frequentDestinationMidleware.getDetailFrequentDestinations, {
        id,
    });
    if (response.type === "W") {
        yield put(actions.loadListFailure());
        yield put(replace("/contactDestination"));
        const message = credentialsErrorMessage(response.data?.data);
        yield put(
            notificationActions.showNotification(message || i18n.get("global.unexpectedError"), "error", [
                "FrequentDestinations",
            ]),
        );
    } else {
        yield put({
            type: types.LOAD_DETAIL_SUCCESS,
            frequentDestinations: response.data.data.frequentDestinations,
        });
    }
}

function* deleteFrequentDestinationPreview(params) {
    const { idFrequentDestination, idTransaction = null } = params.params;
    const response = yield call(frequentDestinationMidleware.deleteFrequentDestinationPreview, {
        idFrequentDestination,
    });
    if (response.type === "W") {
        yield put(actions.deleteFrequentDestinationPreviewFailure());
        yield put(
            notificationActions.showNotification(i18n.get("global.unexpectedError"), "error", [
                "frequentDestinationsDetail",
            ]),
        );
    } else {
        const destinationDeleteParams = idTransaction
            ? {
                  ...response.data.data.frequentDestination,
                  idTransaction,
              }
            : response.data.data.frequentDestination;
        yield put({
            type: types.DELETE_FREQUENT_DESTINATION_PREVIEW_SUCCESS,
            destinationDelete: destinationDeleteParams,
        });
        yield put(
            push(
                // eslint-disable-next-line max-len
                `/contactDestination/${response.data.data.frequentDestination.idFrequentDestination}/delete/confirmation`,
            ),
        );
    }
}

function* deleteFrequentDestination({ frequentDestinationDelete, formikBag, credentialType }) {
    try {
        const response = yield call(
            frequentDestinationMidleware.deleteFrequentDestinationRequenst,
            frequentDestinationDelete,
        );

        if (response.type === "W") {
            yield put(actions.frequentDestinationCreateFailure());
            if (response.data.code === "COR020W") {
                if (response.data?.data?.password) {
                    const msj =
                        credentialType === "password"
                            ? "preferences.password.password.incorrect"
                            : "login.step1.secondFactor.invalid";
                    yield put(
                        notificationActions.showNotification(i18n.get(msj), "error", [
                            "frequentDestinationDeleteConfirmation",
                        ]),
                    );
                    formikBag.setErrors({
                        credential: i18n.get(msj),
                    });
                }
            } else {
                yield put(
                    notificationActions.showNotification(response.data.message, "error", [
                        "frequentDestinationDeleteConfirmation",
                    ]),
                );
            }
        } else {
            if (response.data.code !== "API516W") {
                if (response.data.code === INSUFFICIENT_SIGNATURES) {
                    yield put(
                        notificationActions.showNotification(
                            i18n.get("frequentDestination.delete.pending"),
                            "success",
                            ["FrequentDestinations"],
                        ),
                    );
                } else {
                    yield put(
                        notificationActions.showNotification(
                            i18n.get("frequentDestination.delete.success"),
                            "success",
                            ["FrequentDestinations"],
                        ),
                    );
                }
            }
            yield put(actions.deleteFrequentDestinationSuccess());
            yield put({
                type: types.CONTACT_TICKET_SUCCESS,
                contactTicket: {
                    status: ticketStates[response.data.code],
                    ...response.data,
                },
            });
        }
    } finally {
        formikBag.setSubmitting(false);
    }
}

function* dowloadFrequentDestinations() {
    const response = yield call(frequentDestinationMidleware.dowloadFrequentDestinations);
    const { content, fileName } = response?.data.data;
    if (response.type === "W") {
        yield put(actions.downloadListFrequentDestinationsFailure());
        yield put(
            notificationActions.showNotification(i18n.get("global.unexpectedError"), "error", ["FrequentDestinations"]),
        );
    } else {
        yield put(
            notificationActions.showNotification(i18n.get("global.successDownload"), "success", [
                "FrequentDestinations",
            ]),
        );
        yield call(delay, 3000);
        downloadPdf(fileName, content);
        yield put(actions.downloadListFrequentDestinationsSuccess());
    }
}

function* getCbuAccountInformation(params) {
    // eslint-disable-next-line no-unused-vars
    const { cbu, email, name, schedule } = params.params;
    const { formikBag } = params;
    const validateOnlyCbu = true;
    const response = yield call(
        frequentDestinationMidleware.createDestinationPreview,
        cbu,
        email,
        name,
        validateOnlyCbu,
    );
    formikBag.setSubmitting(false);
    if (response.data.code === "COR000I") {
        const { frequentDestination } = response.data.data;
        const {
            cbu: recipientCbu,
            recipientName,
            isOwn,
            recipientDocumentType,
            recipientDocumentNumber,
        } = frequentDestination;
        yield put({
            type: debinTypes.ADD_TO_CBU_LIST,
            newCbu: {
                cbu: recipientCbu,
                document: `${recipientDocumentType || ""} ${recipientDocumentNumber || ""}`,
                headline: recipientName,
                isOwn,
                reference: name,
                email,
                amount: { amount: null },
                comprobante: null,
                schedule,
            },
        });
    } else if (response.data.data.NO_FIELD) {
        formikBag.setErrors({ receiver: response.data.data.NO_FIELD });
    }
}

function* frequentDestinationCreatePreview({ params, formikBag = null }) {
    const { idTransaction = "" } = params;
    let response;
    try {
        if (idTransaction) {
            response = yield call(
                frequentDestinationMidleware.createDestinationPreview,
                params.cbu,
                params.recipientEmail,
                params.name,
            );
        } else {
            const { cbu, email, name } = params;
            response = yield call(frequentDestinationMidleware.createDestinationPreview, cbu, email, name);
        }

        if (response.type === "W") {
            const scopeToShow = idTransaction ? "FrequentDestinationsTicket" : "FrequentDestinationsCreate";
            yield put(actions.frequentDestinationCreatePreviewFailed());
            if (response.data.data.NO_FIELD) {
                yield put(notificationActions.showNotification(response.data.data.NO_FIELD, "error", [scopeToShow]));
            } else {
                yield put(
                    notificationActions.showNotification(i18n.get("global.unexpectedError"), "error", [scopeToShow]),
                );
            }
        } else {
            const responseCredentials = yield call(
                form.listCredentialsGroups,
                null,
                "administration.signatures.create.send",
            );
            const credentialGroupsData = responseCredentials.data.data.groups;
            const newDestinationParams = idTransaction
                ? {
                      ...response.data.data,
                      frequentDestination: { ...response.data.data.frequentDestination, idTransaction },
                  }
                : response.data.data;

            if (newDestinationParams?.frequentDestination?.cbuBlocked) {
                yield put(
                    notificationActions.showNotification(i18n.get("coelsa.cbuBlocked"), "warning", [
                        "frequentDestinationCreateConfirm",
                    ]),
                );
            }
            yield put(actions.frequentDestinationCreatePreviewSuccess(newDestinationParams, credentialGroupsData));
            yield put(push(`/contactDestination/confirm/${response.data.data.frequentDestination.cbu}`));
        }
    } finally {
        if (formikBag) {
            formikBag.setSubmitting(false);
        }
    }
}

function* frequentDestinationCreate({ frequentDestination, formikBag, credentialType }) {
    try {
        const response = yield call(frequentDestinationMidleware.createDestination, frequentDestination);
        if (response.type === "W") {
            if (response.data.code !== "COR020W") {
                yield put(
                    notificationActions.showNotification(response.data.message, "error", [
                        "frequentDestinationCreateConfirm",
                    ]),
                );
            } else {
                formikBag.setErrors(response.data.data);
                yield put(
                    notificationActions.showNotification(response.data.message, "error", [
                        "frequentDestinationCreateConfirm",
                    ]),
                );
                if (response.data?.data?.password || response.data?.data?.otp) {
                    const msj =
                        credentialType === "password"
                            ? "preferences.password.password.incorrect"
                            : "login.step1.secondFactor.invalid";
                    yield put(
                        notificationActions.showNotification(i18n.get(msj), "error", [
                            "frequentDestinationCreateConfirm",
                        ]),
                    );
                    formikBag.setErrors({
                        credential: i18n.get(msj),
                    });
                }
            }
        } else {
            /* yield put({
                type: types.CONTACT_TICKET_SUCCESS,
                contactTicket: {
                    status: ticketStates[response.data.code],
                    ...response.data,
                },
            }); */
            if (ticketStates[response.data.code] === ticketStates.COR000I) {
                yield put(
                    notificationActions.showNotification(i18n.get("frequentDestination.create.success"), "success", [
                        "FrequentDestinations",
                    ]),
                );
            }
            yield put(actions.frequentDestinationCreateSuccess(response.data.data));
            yield put(replace(`/contactDestination`));
        }
    } finally {
        formikBag.setSubmitting(false);
    }
}

function* modifyFrequentDestinationPre(params) {
    const { idFrequentDestination } = params;
    const response = yield call(frequentDestinationMidleware.modifyFrequentDestinationPre, idFrequentDestination);
    if (response.type === "W") {
        yield put(actions.modifyFrequentDestinationPreFailure());
        yield put(
            notificationActions.showNotification(i18n.get("global.unexpectedError"), "error", [
                "frequentDestinationsDetail",
            ]),
        );
    } else {
        yield put({
            type: types.MODIFY_FREQUENT_DESTINATION_PRE_SUCCESS,
            destinationModify: response.data.data.frequentDestination,
        });
    }
}

function* modifyFrequentDestinationPreview(params) {
    const { idFrequentDestination, name, recipientEmail, idTransaction = null } = params.params;
    const requestParams = {
        name,
        recipientEmail,
        idFrequentDestination,
    };
    const response = yield call(frequentDestinationMidleware.modifyFrequentDestinationPreview, requestParams);
    if (response.type === "W") {
        yield put(actions.modifyFrequentDestinationPreviewFailure());
        if (response.data.data.NO_FIELD) {
            yield put(
                notificationActions.showNotification(response.data.data.NO_FIELD, "error", [
                    "FrequentDestinationsModify",
                ]),
            );
        } else {
            yield put(
                notificationActions.showNotification(i18n.get("global.unexpectedError"), "error", [
                    "FrequentDestinationsModify",
                ]),
            );
        }
    } else {
        const { name: responseName, recipientEmail: responseEmail, idFrequentDestination: id } = response.data.data;
        const destinationModify = idTransaction
            ? {
                  ...response.data.data.frequentDestination,
                  name: responseName,
                  recipientEmail: responseEmail,
                  idFrequentDestination: id,
                  idTransaction,
              }
            : {
                  ...response.data.data.frequentDestination,
                  name: responseName,
                  recipientEmail: responseEmail,
                  idFrequentDestination: id,
              };
        yield put({
            type: types.MODIFY_FREQUENT_DESTINATION_PREVIEW_SUCCESS,
            destinationModify,
        });
        yield put(push(`/contactDestination/${id}/modify/confirmation`));
    }
}

function* modifyFrequentDestinationRequest({ frequentDestinationModify, formikBag, credentialType }) {
    try {
        const response = yield call(frequentDestinationMidleware.modifyFrequentDestination, frequentDestinationModify);
        if (response.type === "W") {
            yield put(actions.modifyFrequentDestinationFailure());
            if (response.data.code === "COR020W") {
                yield put(
                    notificationActions.showNotification(response.data?.message, "error", [
                        "frequentDestinationModifyConfirm",
                    ]),
                );
                if (response.data?.data?.password) {
                    const msj =
                        credentialType === "password"
                            ? "preferences.password.password.incorrect"
                            : "login.step1.secondFactor.invalid";
                    yield put(
                        notificationActions.showNotification(i18n.get(msj), "error", [
                            "frequentDestinationModifyConfirm",
                        ]),
                    );
                    formikBag.setErrors({
                        credential: i18n.get(msj),
                    });
                }
            } else {
                yield put(
                    notificationActions.showNotification(response.data.message, "error", [
                        "frequentDestinationModifyConfirm",
                    ]),
                );
            }
        } else {
            if (response.data.code !== "API516W") {
                if (response.data.code === INSUFFICIENT_SIGNATURES) {
                    yield put(
                        notificationActions.showNotification(
                            i18n.get("frequentDestination.modify.pending"),
                            "success",
                            ["FrequentDestinationsTicket"],
                        ),
                    );
                } else {
                    yield put(
                        notificationActions.showNotification(
                            i18n.get("frequentDestination.modify.success"),
                            "success",
                            ["FrequentDestinationsTicket"],
                        ),
                    );
                }
            }

            yield put(actions.modifyFrequentDestinationSuccess());
            yield put({
                type: types.CONTACT_TICKET_SUCCESS,
                contactTicket: {
                    status: ticketStates[response.data.code],
                    ...response.data,
                },
            });
        }
    } finally {
        formikBag.setSubmitting(false);
    }
}

function* createFrequentDestinationTransferPre(params) {
    const response = yield call(frequentDestinationMidleware.createFrequentDestinationTransferPre, params);
    if (response.type === "W") {
        yield put(actions.createFrequentDestinationTransferPreFailure());
        yield put(notificationActions.showNotification(i18n.get("global.unexpectedError"), "error", ["form"]));
    } else {
        yield put(actions.createFrequentDestinationTransferPreSuccess(response.data.data));
    }
}

function* verifyFrequentDestination(params) {
    const { idEnvironment, cbu } = params;
    const response = yield call(frequentDestinationMidleware.getListFrequentDestinations, idEnvironment, cbu);
    if (response.type === "W") {
        yield put(actions.getFrequentDestinationFailure());
        yield put(notificationActions.showNotification(i18n.get("global.unexpectedError"), "error", ["form"]));
    } else {
        yield put(actions.getFrequentDestinationSuccess(response.data.data));
    }
}

function* downloadDestinations(params) {
    const { search, format, pageNumber, totalPages } = params;

    const response = yield call(frequentDestinationMidleware.downloadDestinations, {
        search,
        format,
        pageNumber,
        totalPages,
    });

    if (response.type === "W") {
        yield put(actions.getFrequentDestinationFailure());
        yield put(notificationActions.showNotification(i18n.get("global.unexpectedError"), "error", ["form"]));
    } else {
        const { fileName, content } = response.data.data;
        downloadPdf(fileName, content);
        yield put({
            type: types.FREQUENT_DESTINATIONS_DOWLOAD_SUCCESS,
        });
    }
}

function* validateCbuBlocked({ creditCbu }) {
    try {
        const response = yield call(transfersMiddleware.accountInfoByCbu, { creditCbu });

        if (response.type === "W") {
            const message = credentialsErrorMessage(response.data?.data);

            yield put(
                notificationActions.showNotification(message || response.data?.message, "error", [
                    "otherTransfers",
                    "debinPayments",
                ]),
            );

            const cbuBlocked = response.data?.data?.CBU_BLOCKED ? true : false;
            yield put({
                type: types.VALIDATE_CBU_BLOCKED_RESPONSE,
                cbuBlocked,
            });
        } else {
            yield put({
                type: types.VALIDATE_CBU_BLOCKED_RESPONSE,
                cbuBlocked: false,
            });
        }
    } catch (error) {
        yield put({
            type: types.VALIDATE_CBU_BLOCKED_RESPONSE,
            cbuBlocked: false,
        });
    }
}
